import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import logger from 'src/utils/logger';
import type { components, paths } from '../generated/v1';
import { $api, $apiFetchClient } from './api';
import { hideSpinnerToast, showSpinnerToast } from '../utils/spinnerToaster';

export type InboundOrder = components['schemas']['InboundOrder'];
export type InboundOrderList = components['schemas']['InboundOrderList'];
export type InboundOrderCreate = components['schemas']['InboundOrderCreate'];
export type InboundOrderUpdate = components['schemas']['InboundOrderUpdate'];
export type InboundCancelRequest = components['schemas']['InboundCancelRequest'];
export type InboundPackage = components['schemas']['InboundPackage'];
export type InboundPackageLines = components['schemas']['InboundPackageExtend'];
export type PackageLines = components['schemas']['PackageLines'];
export type InboundBackorderLine = components['schemas']['InboundBackorderLine'];
export type InboundPhoto = components['schemas']['InboundPhoto'];

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['get', '/stock/inbound'] });
  queryClient.invalidateQueries({ queryKey: ['get', '/stock/inbound/{inbound_order_id}'] });
}

// Inbound cancels for FlowSync-routed orders are REQUESTED at the warehouse (async
// saga) — the mutation response is just an ack, `inbound_state` only flips once the
// WMS confirms (usually within seconds) or stays unchanged on rejection. The
// immediate `invalidate()` above therefore often re-fetches a still-stale state.
// This schedules a single delayed re-invalidation to pick up the confirmation
// without polling or an optimistic update.
export const scheduleInboundRefetch = (client: QueryClient, delayMs = 4000) => {
  window.setTimeout(() => {
    client.invalidateQueries({ queryKey: ['get', '/stock/inbound'] });
    client.invalidateQueries({ queryKey: ['get', '/stock/inbound/{inbound_order_id}'] });
  }, delayMs);
};

export const useInboundOrdersQuery = (
  params: paths['/stock/inbound']['get']['parameters']['query'],
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', '/stock/inbound', {
    params: {
      query: params,
    },
    ...options,
  });
};

export const useInboundOrderQuery = (
  params: { path: { inbound_order_id: number }; query?: any },
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', `/stock/inbound/{inbound_order_id}`, {
    params,
    ...options,
  });
};

export const useInboundOrderPackageQuery = (
  params: { path: { inbound_order_id: number; package_id: number }; query?: any },
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', `/stock/inbound/{inbound_order_id}/package/{package_id}`, {
    params,
    ...options,
  });
};

// Inbound-Draft spec 2026-11-03 §2.1: a pending cancel request reads as its own state.
export const isCancelRequested = (order?: { inbound_state?: { key?: string | null } | null } | null) =>
  order?.inbound_state?.key === 'cancel_requested';

export const useInboundOrderMutations = () => {
  const queryClient = useQueryClient();
  const onSuccess = () => invalidate(queryClient);

  const createMutation = $api.useMutation('post', '/stock/inbound', { onSuccess });
  const cancelMutation = $api.useMutation('put', '/utility/bulk_action/{model}/cancel', { onSuccess });
  // Inbound-Draft spec 2026-11-03 §6: single-order actions on the draft lifecycle.
  const updateMutation = $api.useMutation('put', '/stock/inbound/{inbound_order_id}', { onSuccess });
  const confirmMutation = $api.useMutation('post', '/stock/inbound/{inbound_order_id}/confirm', { onSuccess });
  const cancelOrderMutation = $api.useMutation('post', '/stock/inbound/{inbound_order_id}/cancel', { onSuccess });
  const draftMutation = $api.useMutation('post', '/stock/inbound/{inbound_order_id}/draft', { onSuccess });
  const duplicateMutation = $api.useMutation('post', '/stock/inbound/{inbound_order_id}/duplicate', { onSuccess });

  const path = (id: number) => ({ params: { path: { inbound_order_id: id } } });

  const updateInboundOrderAsync = (id: number, body: InboundOrderUpdate) =>
    updateMutation.mutateAsync({ ...path(id), body });
  const confirmInboundOrderAsync = (id: number) => confirmMutation.mutateAsync(path(id));
  const cancelInboundOrderAsync = (id: number) => cancelOrderMutation.mutateAsync(path(id));
  const setInboundOrderDraftAsync = (id: number) => draftMutation.mutateAsync(path(id));
  const duplicateInboundOrderAsync = (id: number, name?: string) =>
    duplicateMutation.mutateAsync({ ...path(id), body: name ? { name } : null });

  // Server-side duplicate (replaces the client-side rebuild from one GET per
  // package, which lost labeling and the note). Sequential on purpose: one new
  // draft per source, in selection order.
  const duplicateInboundOrders = async (ids: number[]) => {
    const created = await ids.reduce<Promise<InboundOrder[]>>(
      async (acc, id) => [...(await acc), await duplicateInboundOrderAsync(id)],
      Promise.resolve([]),
    );
    invalidate(queryClient);
    return created;
  };

  return {
    createInboundOrder: createMutation.mutate,
    createInboundOrderAsync: createMutation.mutateAsync,
    updateInboundOrderAsync,
    confirmInboundOrderAsync,
    cancelInboundOrderAsync,
    setInboundOrderDraftAsync,
    duplicateInboundOrderAsync,
    duplicateInboundOrders,
    cancelMutation,
  };
};

export const useInboundBackorderCancelMutation = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/stock/inbound/{inbound_order_id}/backorder/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const getInboundDeliverySlip = async (orderId: number) => {
  showSpinnerToast(t('inbound.get_delivery_slip'));

  try {
    const resp = await $apiFetchClient.GET('/stock/inbound/{inbound_order_id}/pdf/', {
      params: {
        path: {
          inbound_order_id: orderId,
        },
      },
      parseAs: 'blob',
    });

    return resp.data;
  } catch (error) {
    logger.error('Failed to fetch delivery slip PDF', error);
    throw error;
  } finally {
    hideSpinnerToast();
  }
};

export const getInboundPackageDeliverySlip = async (orderId: number, package_id: number) => {
  showSpinnerToast(t('inbound.get_delivery_slip'));

  try {
    const resp = await $apiFetchClient.GET('/stock/inbound/{inbound_order_id}/{package_id}/pdf/', {
      params: {
        path: {
          inbound_order_id: orderId,
          package_id,
        },
      },
      parseAs: 'blob',
    });

    return resp.data;
  } catch (error) {
    logger.error('Failed to fetch delivery slip PDF', error);
    throw error;
  } finally {
    hideSpinnerToast();
  }
};
